import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Spacing, Text, Gap } from '@reservamos/elements';
import { When } from 'react-if';
import useWhitelabelEnvs from 'hooks/whitelabel/useWhitelabelEnvs';
import LabelLoading from '../../../ui/molecules/LabelLoading';
import InfoText from '../../../ui/atoms/InfoText';
import AvailableCategoriesList from './AvailableCategoriesLis';
import NotAvailableCategoriesList from './NotAvailableCategoriesLis';

const propTypes = {
  name: PropTypes.string.isRequired,
  passengerCategory: PropTypes.string,
  isFirstPassenger: PropTypes.bool.isRequired,
  busCategories: PropTypes.array.isRequired,
  isUpdating: PropTypes.bool.isRequired,
  isDisabled: PropTypes.bool,
  isExchange: PropTypes.bool,
  isOpenTicket: PropTypes.bool,
  passengerBusCategory: PropTypes.string,
  initialBusCategory: PropTypes.string,
  restrictedCategories: PropTypes.arrayOf(PropTypes.string),
};

const defaultProps = {
  isDisabled: false,
  restrictedCategories: [],
};

const PassengerBusCategories = ({
  name,
  passengerCategory,
  isFirstPassenger,
  busCategories,
  isUpdating,
  isDisabled,
  isExchange,
  isOpenTicket,
  passengerBusCategory,
  initialBusCategory,
  restrictedCategories,
}) => {
  const { features } = useSelector((state) => state.whitelabelConfig);
  const { t } = useTranslation('seats');
  const form = useSelector((state) => state.form);
  const { brand } = useWhitelabelEnvs();
  const {
    passengers: {
      values: { passengers },
    },
  } = form;

  let allowedCategories = busCategories.filter(
    ({ type }) => !features.SPECIAL_CATEGORIES?.includes(type),
  );

  // Filter by SPECIFIC_CATEGORIES if configured
  // prettier-ignore
  if (features.SPECIFIC_CATEGORIES?.length > 0) {
    allowedCategories = allowedCategories.filter(({ type }) =>
      features.SPECIFIC_CATEGORIES.includes(type));
  }

  // the first passenger can't be a minor
  if (isFirstPassenger && !isExchange) {
    allowedCategories = allowedCategories.filter((category) => category.type !== 'minor');
  }

  // if child, restrict to minor & general
  if (passengerCategory === 'child') {
    allowedCategories = allowedCategories.filter(
      ({ availability, type }) => availability > 0 && ['minor', 'general'].includes(type),
    );
  }

  const allowedCategoryTypes = allowedCategories.map(({ type }) => type);

  const getAvailableCategories = () => {
    const availableCategories = {};
    allowedCategories.forEach((category) => {
      // eslint-disable-next-line prettier/prettier
      availableCategories[category.type] = passengers.filter(
        (passenger) => passenger.busCategory === category.type,
      ).length;
    });
    return availableCategories;
  };

  const getIsDisabled = (type, availability) => {
    const rest = availability - getAvailableCategories()[type];

    return rest === 0 && passengerBusCategory !== type;
  };

  const renderRadioButton = useCallback(() => {
    const availableCategoriesFiltered = allowedCategories
      .filter(
        ({ type, availability, blocked, soldOut }) =>
          !soldOut && !getIsDisabled(type, availability) && !blocked,
      )
      .map(({ type }) => {
        /**
         * This condition is to allow the user to change the category on exchanges,
         * Only available to change to the general category if enabled.
         * There is one scenario where WS returns an error on some categories, so we need to allow the user to change it.
         * Only available if the user bought a different category than the default one (general aka "Adulto").
         * Ticket: https://reservamossaas.atlassian.net/browse/EN-1368
         * Ticket: https://reservamossaas.atlassian.net/browse/EN-1324
         */
        const canChangeOnlyToGeneral =
          isExchange && !['general', initialBusCategory].includes(type);

        const isRestrictedBySeat = restrictedCategories.includes(type);

        return {
          type,
          disabled:
            isRestrictedBySeat ||
            (features.ALLOW_CHANGE_CATEGORY_ON_EXCHANGE ? canChangeOnlyToGeneral : isDisabled),
        };
      });
    const notAvailableCategoriesFiltered = allowedCategories.filter(
      ({ type, availability, blocked, soldOut }) =>
        soldOut || getIsDisabled(type, availability) || blocked,
    );

    return (
      <>
        <AvailableCategoriesList name={name} categories={availableCategoriesFiltered} />
        {notAvailableCategoriesFiltered.length ? (
          <NotAvailableCategoriesList categories={notAvailableCategoriesFiltered} />
        ) : null}
      </>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowedCategories, isDisabled, name, passengers, restrictedCategories]);

  // if open ticket or exchange, don't show legend and hide category selection
  if (features.JUST_ADULT_OPEN_TICKET && (isOpenTicket || isExchange)) {
    return <InfoText>{t('categories_not_available')}</InfoText>;
  }

  // if infant or 1 category, don't show this field
  if (passengerCategory === 'infant' || busCategories.length < 2)
    return features.VALID_CATEGORIES?.length && !features.HIDE_CATEGORIES_NOT_AVAILABLE_INFO ? (
      <InfoText>{t('categories_not_available')}</InfoText>
    ) : null;

  features.VALID_CATEGORIES.forEach((category) => {
    if (isFirstPassenger && category === 'minor') {
      return;
    }
    if (!allowedCategoryTypes.includes(category)) {
      allowedCategories.push({ type: category, soldOut: true });
    }
  });

  if (isUpdating) {
    return (
      <Spacing vertical size="S">
        <Text size="S" weight="semibold">
          {t('category', { context: brand })}
        </Text>
        <LabelLoading text={t('general:loading')} />
      </Spacing>
    );
  }

  return (
    <Spacing vertical>
      <Spacing vertical size="S">
        <Text size="S" weight="semibold">
          {t('category', { context: brand })}
        </Text>
        <Gap alignItems="center">{renderRadioButton()}</Gap>
        <When condition={restrictedCategories.length > 0}>
          <InfoText>{t('first_row_restricted_categories')}</InfoText>
        </When>
        <When condition={features.SHOW_INFANTS_DISCLAIMER}>
          {isFirstPassenger && <InfoText>{t('minor_disclaimer')}</InfoText>}
        </When>
        <When condition={features.SHOW_INAPAM_DISCLAIMER}>
          {isFirstPassenger && <InfoText>{t('inapam_disclaimer')}</InfoText>}
        </When>
        <When condition={features.SHOW_GRATUITY_SINGLE_USE_DISCLAIMER}>
          {isFirstPassenger && <InfoText>{t('gratuity_single_use_disclaimer')}</InfoText>}
        </When>
      </Spacing>
    </Spacing>
  );
};

PassengerBusCategories.propTypes = propTypes;
PassengerBusCategories.defaultProps = defaultProps;

export default PassengerBusCategories;
